import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from 'src/app/core/services/payment.service';
import { UtilService } from 'src/app/core/services/util.service';
import { FeeSummary } from 'src/app/core/models/fee.model';

@Component({
  selector: 'app-standalone-payment-confirmation',
  templateUrl: './standalone-payment-confirmation.component.html',
  styleUrls: ['./standalone-payment-confirmation.component.scss'],
})
export class StandalonePaymentConfirmationComponent implements OnInit {
  feeSummary: FeeSummary | null = null;
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  paymentStatus: string = '';
  token: string = '';
  isRefreshing: boolean = false;

  constructor(
    private paymentService: PaymentService,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const feeSummaryId = params['feeSummaryId'];
      const appId = params['appId'];

      if (feeSummaryId && appId) {
        this.loadPaymentDetails(feeSummaryId, appId);
      } else {
        // feeSummaryId and appId are required - provided via query params from callback
        this.showError('No payment information found. Missing feeSummaryId or appId.');
      }
    });
  }

  private loadPaymentDetails(feeSummaryId: string, appId: string): void {
    this.paymentService.getStandalonePayment(feeSummaryId, appId).subscribe({
      next: (response: any) => {
        this.feeSummary = new FeeSummary();
        this.feeSummary.feeSummaryId = response.feeSummaryId;
        this.feeSummary.appId = response.appId || appId;
        this.feeSummary.transactionId = response.transactionId;
        this.feeSummary.transactionDate = response.transactionDate;
        this.feeSummary.feeTotal = response.feeTotal;
        this.feeSummary.feeTotalPaid = response.feeTotalPaid;
        this.feeSummary.creditUsed = response.creditUsed || 0;
        this.feeSummary.balanceAmount = response.balanceAmount;
        this.feeSummary.items = response.items || [];
        this.paymentStatus = response.status || 'pending';
        this.token = response.token || '';
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading payment details:', err);
        this.showError('Unable to load payment details');
      },
    });
  }

  refreshPaymentStatus(): void {
    if (!this.feeSummary?.feeSummaryId || !this.feeSummary?.appId || !this.token) {
      return;
    }

    this.isRefreshing = true;
    this.paymentService.verifyStandalonePayment(this.token, this.feeSummary.feeSummaryId, this.feeSummary.appId).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.feeSummary!.transactionId = response.transactionId;
          this.feeSummary!.transactionDate = response.transactionDate;
          this.feeSummary!.feeTotalPaid = response.feeTotalPaid;
          this.feeSummary!.creditUsed = response.creditUsed || 0;
          this.feeSummary!.balanceAmount = response.balanceAmount;
          this.paymentStatus = response.status || 'completed';
        }
        this.isRefreshing = false;
      },
      error: (err) => {
        console.error('Error refreshing payment status:', err);
        this.isRefreshing = false;
      },
    });
  }

  isPaymentProcessing(): boolean {
    // Any status that's not completed is considered "processing"
    // This includes: processing, pending, empty, or unknown statuses
    return !['completed', 'failed', 'cancelled'].includes(this.paymentStatus);
  }

  isPaymentCompleted(): boolean {
    return this.paymentStatus === 'completed';
  }

  isPaymentFailed(): boolean {
    return ['failed', 'cancelled'].includes(this.paymentStatus);
  }

  private showError(message: string): void {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage = message;
  }

  getDescription(): string {
    if (this.feeSummary?.items && this.feeSummary.items.length > 0) {
      return this.feeSummary.items[0].name || 'Payment';
    }
    return 'Payment';
  }

  printConfirmation(): void {
    window.print();
  }

  goToDashboard(): void {
    this.router.navigate(['app']);
  }

  makeAnotherPayment(): void {
    this.router.navigate(['app/standalone-payment']);
  }
}
